import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';
import type { SummaryResponse, TransactionRow } from './types';
import { fmtDate, todayISO, yesterdayISO } from './lib/format';
import { SummaryCards } from './components/SummaryCards';
import { AccountBreakdown } from './components/AccountBreakdown';
import { CategoryBreakdown } from './components/CategoryBreakdown';
import { FlaggedList } from './components/FlaggedList';
import { TransactionsTable } from './components/TransactionsTable';
import { ManualEntryModal } from './components/ManualEntryModal';
import { ConnectCardButton } from './components/ConnectCardButton';

// Hard-coded list mirrors what's in migrations/0001_initial_schema.sql.
// (The server also exposes this data — we embed it to avoid an extra round-trip.)
const CATEGORIES = [
  { slug: 'software',       name: 'Software / SaaS',              kind: 'expense' },
  { slug: 'shipping',       name: 'Shipping & Postage',           kind: 'expense' },
  { slug: 'materials',      name: 'Materials & Supplies',         kind: 'expense' },
  { slug: 'printing',       name: 'Printing & Production',        kind: 'expense' },
  { slug: 'advertising',    name: 'Advertising & Marketing',      kind: 'expense' },
  { slug: 'meals',          name: 'Meals & Entertainment',        kind: 'expense' },
  { slug: 'travel',         name: 'Travel',                       kind: 'expense' },
  { slug: 'fees',           name: 'Bank / Payment Fees',          kind: 'expense' },
  { slug: 'payroll',        name: 'Payroll & Contractors',        kind: 'expense' },
  { slug: 'owner_draw',     name: 'Owner Draw',                   kind: 'expense' },
  { slug: 'taxes',          name: 'Taxes',                        kind: 'expense' },
  { slug: 'other_expense',  name: 'Other Expense',                kind: 'expense' },
  { slug: 'uncategorized',  name: 'Uncategorized',                kind: 'expense' },
  { slug: 'revenue_stripe', name: 'Revenue — Stripe',             kind: 'revenue' },
  { slug: 'revenue_square', name: 'Revenue — Square',             kind: 'revenue' },
  { slug: 'revenue_other',  name: 'Revenue — Other (checks/ACH)', kind: 'revenue' },
  { slug: 'refund',         name: 'Refund',                       kind: 'revenue' },
];

export default function App() {
  const [date, setDate] = useState(yesterdayISO());
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [txns, setTxns] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, t] = await Promise.all([
        api.summary(date),
        api.transactions({ from: date, to: date, limit: 200 }),
      ]);
      setSummary(s);
      setTxns(t.transactions);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setError(
          'Not authenticated. Finish Cloudflare Access setup on this domain ' +
          'and the Worker domain so your email-login lets API calls through. ' +
          'Until then, the dashboard will show this banner.'
        );
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { void load(); }, [load]);

  async function runSync() {
    setSyncing(true);
    try {
      await api.sync();
      await load();
    } finally {
      setSyncing(false);
    }
  }

  const lastSync = useMemo(() => {
    return summary?.byAccount?.map(a => null).filter(Boolean)[0] ?? null;
  }, [summary]);

  return (
    <div className="min-h-screen bg-slate-50 text-ink-900">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">UTP Financials</h1>
            <p className="text-xs text-slate-500">{fmtDate(date)}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white"
              max={todayISO()}
            />
            <button onClick={() => setDate(yesterdayISO())} className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50">Yesterday</button>
            <button onClick={() => setDate(todayISO())}     className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50">Today</button>
            <ConnectCardButton onConnected={load} />
            <button
              onClick={() => setShowManual(true)}
              className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
            >
              Add manual entry
            </button>
            <button
              onClick={runSync}
              disabled={syncing}
              className="text-sm px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm p-3 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : summary ? (
          <>
            <SummaryCards totals={summary.totals} />

            <FlaggedList
              items={summary.flagged}
              onChange={load}
              categoryOptions={CATEGORIES.filter(c => c.kind === 'expense')}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AccountBreakdown rows={summary.byAccount} />
              <CategoryBreakdown rows={summary.byCategory} />
            </div>

            <div>
              <h2 className="text-sm font-semibold text-slate-700 mb-2">All transactions — {fmtDate(date)}</h2>
              <TransactionsTable rows={txns} />
            </div>
          </>
        ) : null}

        {lastSync && <div className="text-xs text-slate-400 text-center">Last sync: {lastSync}</div>}
      </main>

      <ManualEntryModal
        open={showManual}
        onClose={() => setShowManual(false)}
        categories={CATEGORIES}
        onSaved={load}
      />
    </div>
  );
}
